import { FC } from "react";
import { useTranslation } from "react-i18next";
import { Stack, MenuItem, ListItemText, ListItemIcon, ListItemButton, Button, Typography, Divider, Avatar, Menu, Grid, SvgIcon, ListItem, MenuList } from "@mui/material";
import { SvgIconComponent } from "@mui/icons-material";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import SupportAgentRoundedIcon from "@mui/icons-material/SupportAgentRounded";
import BookRoundedIcon from "@mui/icons-material/BookRounded";
import { capitalize } from "lodash";
import { MAIN_DOC_ITEMS, SUB_DOC_ITEMS } from "./config";
import instanceZUID from "../../../utility/instanceZUID";
interface GlobalDocsMenuProps {
  open: boolean;
  onClose: () => void;
  anchorEl: HTMLElement;
}
export const GlobalDocsMenu: FC<GlobalDocsMenuProps> = ({
  open,
  onClose,
  anchorEl
}) => {
  const {
    t
  } = useTranslation();
  const mainApp = location.pathname.split("/")[1];
  const subApp = location.pathname.split("/")[2];
  const section = mainApp === "reports" ? `reports/${subApp}` : mainApp;
  const subDocs = SUB_DOC_ITEMS[section] || SUB_DOC_ITEMS.default;
  const handleOpenUrl = (url: string) => {
    onClose();
    window.open(url, "_blank", "noopener");
  };
  return <Menu open={open} anchorEl={anchorEl} onClose={onClose} slotProps={{
    paper: {
      sx: {
        width: 520
      }
    }
  }} MenuListProps={{
    sx: {
      pt: 0
    }
  }} anchorOrigin={{
    vertical: -12,
    horizontal: "left"
  }} transformOrigin={{
    vertical: "bottom",
    horizontal: "left"
  }} data-sentry-element="Menu" data-sentry-component="GlobalDocsMenu" data-sentry-source-file="index.tsx">
      <Stack direction="row" gap={1.5} alignItems="center" height={64} borderBottom="1px solid" py={2.25} px={2} boxSizing="border-box" sx={{
      borderColor: "divider"
    }} data-sentry-element="Stack" data-sentry-source-file="index.tsx">
        <Avatar sx={{
        bgcolor: "grey.100"
      }} data-sentry-element="Avatar" data-sentry-source-file="index.tsx">
          <MenuBookRoundedIcon color="action" data-sentry-element="MenuBookRoundedIcon" data-sentry-source-file="index.tsx" />
        </Avatar>
        <Typography variant="h5" fontWeight={600} data-sentry-element="Typography" data-sentry-source-file="index.tsx">
          {t("shell.docs")}
        </Typography>
      </Stack>
      <Grid container px={2} data-sentry-element="Grid" data-sentry-source-file="index.tsx">
        {MAIN_DOC_ITEMS.map((docItem, index) => <Grid size={4} key={index} height={106}>
            <ListItemButton sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.75,
          height: "inherit",
          p: 0
        }} onClick={() => handleOpenUrl(docItem.url)}>
              <ListItemIcon sx={{
            justifyContent: "center"
          }}>
                {docItem.iconType === "icon" && <SvgIcon component={docItem.icon as SvgIconComponent} color={docItem.iconColor} sx={{
              width: 32,
              height: 32
            }} />}

                {docItem.iconType === "image" && <img src={docItem.icon as string} alt={t(docItem.textKey)} width={32} height={32} />}
              </ListItemIcon>
              <ListItemText sx={{
            flex: 0
          }} primaryTypographyProps={{
            variant: "caption",
            lineHeight: "18px",
            letterSpacing: 0.15,
            align: "center"
          }}>
                {t(docItem.textKey)}
              </ListItemText>
            </ListItemButton>
          </Grid>)}
      </Grid>
      <Divider data-sentry-element="Divider" data-sentry-source-file="index.tsx" />
      <MenuList data-sentry-element="MenuList" data-sentry-source-file="index.tsx">
        <ListItem sx={{
        height: 36
      }} data-sentry-element="ListItem" data-sentry-source-file="index.tsx">
          <ListItemText primaryTypographyProps={{
          fontWeight: 600
        }} data-sentry-element="ListItemText" data-sentry-source-file="index.tsx">
            {t("shell.learnMoreAbout", {
            app: capitalize(mainApp)
          })}
          </ListItemText>
        </ListItem>
        {subDocs?.map((subDocItem, index) => <MenuItem key={index} onClick={() => handleOpenUrl(subDocItem.url)}>
            <ListItemIcon>
              <BookRoundedIcon />
            </ListItemIcon>
            <ListItemText>{t(subDocItem.textKey)}</ListItemText>
          </MenuItem>)}
      </MenuList>
      <Divider data-sentry-element="Divider" data-sentry-source-file="index.tsx" />
      <ListItem sx={{
      height: 36,
      mt: 1
    }} data-sentry-element="ListItem" data-sentry-source-file="index.tsx">
        <ListItemText primaryTypographyProps={{
        fontWeight: 600
      }} data-sentry-element="ListItemText" data-sentry-source-file="index.tsx">
          {t("shell.needHelpContactSupport")}
        </ListItemText>
      </ListItem>
      <ListItem sx={{
      display: "flex",
      gap: 1.5,
      height: 46
    }} data-sentry-element="ListItem" data-sentry-source-file="index.tsx">
        <Button data-cy="SendSupportEmail" size="small" variant="outlined" color="inherit" startIcon={<EmailRoundedIcon color="action" />} onClick={() => handleOpenUrl(`mailto:support@zesty.io?subject=%5BInstance%20ZUID%3A%20${instanceZUID}%5D%20Support%20Request`)} data-sentry-element="Button" data-sentry-source-file="index.tsx">
          {/* eslint-disable-next-line i18next/no-literal-string -- email address */}
          <>support@zesty.io</>
        </Button>
        <Button data-cy="GetHelp" size="small" variant="outlined" color="inherit" startIcon={<SupportAgentRoundedIcon color="action" />} onClick={() => handleOpenUrl(`https://www.zesty.io/instances/${instanceZUID}/support`)} data-sentry-element="Button" data-sentry-source-file="index.tsx">
          {t("shell.contactSupport")}
        </Button>
      </ListItem>
    </Menu>;
};